'use client'

import { cn } from '@/lib/utils'
import { useStoreI18n } from '@/store'
import {
  Typo3SectionSpotSanityAccomodation,
  Typo3SectionSpotSanityCulture,
  Typo3SectionSpotSanityEatDrink,
  Typo3SectionSpotSanityEventList,
  Typo3SectionSpotSanityFreetime,
  Typo3SectionSpotSanityNature,
  Typo3SectionSpotSanityPressroom,
  Typo3SectionSpotSanityService,
  Typo3SectionSpotSanityShopping,
  Typo3SectionSpotSanitySport,
  Typo3SectionSpotSanitySportServices,
  Typo3SectionSpotSanityStory,
  Typo3SectionSpotSanityTour
} from '@/types'
import { Button, Popover, RadioGroup } from '@/ui/components'
import { memo, useEffect } from 'react'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data:
    | Typo3SectionSpotSanityAccomodation
    | Typo3SectionSpotSanityCulture
    | Typo3SectionSpotSanityService
    | Typo3SectionSpotSanityEatDrink
    | Typo3SectionSpotSanityFreetime
    | Typo3SectionSpotSanityNature
    | Typo3SectionSpotSanityShopping
    | Typo3SectionSpotSanitySport
    | Typo3SectionSpotSanitySportServices
    | Typo3SectionSpotSanityEventList
    | Typo3SectionSpotSanityTour
    | Typo3SectionSpotSanityStory
    | Typo3SectionSpotSanityPressroom
  store: any
}

const SpotSanitySorts = ({ data, store, ...props }: Props) => {
  //stores
  const { setSortBy, sortBy } = store()
  const D = useStoreI18n.use.dictionary()

  useEffect(() => {
    if (!sortBy) {
      setSortBy(data.plugin?.sortBy ?? 'random')
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [sortBy, setSortBy])

  const sortGroups = {
    random: [{ key: `${D['fe.plugin.filters.random']}`, value: 'random' }],
    name: [
      { key: `${D['fe.plugin.sortBy.name']} ↓`, value: 'name-desc' },
      { key: `${D['fe.plugin.sortBy.name']} ↑`, value: 'name-asc' }
    ],
    price: [
      { key: `${D['fe.plugin.filters.price']} ↓`, value: 'price-desc' },
      { key: `${D['fe.plugin.filters.price']} ↑`, value: 'price-asc' }
    ],
    date: [
      { key: `${D['fe.plugin.filters.date']} ↓`, value: 'date-desc' },
      { key: `${D['fe.plugin.filters.date']} ↑`, value: 'date-asc' }
    ],
    distance: [
      { key: `${D['be.tour.filter.distance']} ↓`, value: 'distance-desc' },
      { key: `${D['be.tour.filter.distance']} ↑`, value: 'distance-asc' }
    ],
    difficulty: [
      { key: `${D['be.tour.filter.difficulty']} ↓`, value: 'difficulty-desc' },
      { key: `${D['be.tour.filter.difficulty']} ↑`, value: 'difficulty-asc' }
    ],
    duration: [
      { key: `${D['be.tour.filter.duration']} ↓`, value: 'duration-desc' },
      { key: `${D['be.tour.filter.duration']} ↑`, value: 'duration-asc' }
    ]
  }

  const getSorts = () => {
    const sorts: { key: string; value: string }[] = []
    data.plugin?.sortOptions?.forEach((sort) => {
      sorts.push(...sortGroups[sort as keyof typeof sortGroups])
    })
    return sorts
  }

  const filteredSorts = getSorts()
  const selectedSort =
    filteredSorts.find((sort) => sort.value === sortBy) ?? filteredSorts[0]

  useEffect(() => {
    if (!filteredSorts.some((sort) => sort.value === sortBy) && selectedSort) {
      setSortBy(selectedSort.value)
    }
  }, [filteredSorts, selectedSort, setSortBy, sortBy])

  return (
    <Popover
      className={cn(
        'relative z-10 h-full w-fit cursor-pointer justify-self-start'
      )}
      button={
        <Button variant="text" size="s">
          {D['fe.events.sortBy']
            ? `${D['fe.events.sortBy']} ${selectedSort?.key ?? ''}`
            : 'Loading...'}
        </Button>
      }
      {...props}
    >
      <RadioGroup.Root value={sortBy} onValueChange={setSortBy}>
        {filteredSorts?.length > 0 &&
          filteredSorts?.map((sort) => (
            <RadioGroup.Item
              key={sort.key}
              value={sort.value}
              id={`r-${sort.value}`}
            >
              {sort.key}
            </RadioGroup.Item>
          ))}
      </RadioGroup.Root>
    </Popover>
  )
}

export default memo(SpotSanitySorts)
